/**
 * One spinner pattern, used everywhere.
 *
 * The audit found 5+ different spinning-circle implementations (raw
 * div with border + animate-spin, lucide Loader2, ScientificLoader,
 * various sizes and colors). Pick one, use it everywhere.
 *
 * Three sizes:
 *   sm — inline with text (in a button, in a row)
 *   md — in a card body or inline-block
 *   lg — full-page / centered loader
 *
 * Always carries an accessible label so screen readers know what's
 * happening. Default is "Loading"; override via `label` when more
 * context helps (e.g. "Forecasting").
 */
import React from "react";
import { cn } from "@/lib/utils";


export interface SpinnerProps {
  size?: "sm" | "md" | "lg";
  /** Aria-live message for screen readers. */
  label?: string;
  /** When provided, renders the label inline next to the spinner. */
  showLabel?: boolean;
  className?: string;
}


const SIZE_CLASSES = {
  sm: "w-4 h-4 border-2",
  md: "w-6 h-6 border-2",
  lg: "w-10 h-10 border-[3px]",
} as const;


export function Spinner({
  size = "md", label = "Loading", showLabel = false, className,
}: SpinnerProps) {
  const spinner = (
    <span
      role="status"
      aria-live="polite"
      className={cn(
        "inline-block rounded-full border-primary/20 border-t-primary animate-spin",
        SIZE_CLASSES[size],
        className,
      )}
    >
      <span className="sr-only">{label}</span>
    </span>
  );
  if (!showLabel) return spinner;
  return (
    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      {spinner}
      <span>{label}</span>
    </span>
  );
}


/**
 * Centered full-card / full-page variant — drop into the body of a
 * container when you need a loading state that takes up the whole
 * panel rather than being inline.
 */
export function SpinnerBlock({
  label = "Loading", size = "lg",
}: {
  label?: string;
  size?: "md" | "lg";
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
      <Spinner size={size} label={label} />
      <span>{label}</span>
    </div>
  );
}
